import type { ImportResult } from '@/components/import/types/importResult';
import { config } from '@/config';
import { toast } from 'react-toastify';
import 'whatwg-fetch';
import type { IntlShape } from 'react-intl';
import { ErrorType, isCaslForbiddenError } from '@enmon/commons';
import { RESOURCES_TRANSLATED, RESOURCE_ACTIONS_TRANSLATED } from '@/common/messages/user/permissions';
import { TRANSLATED_ERRORS } from '@/common/messages/errors';
import { stringify } from 'qs';
import { isZodIssue, mapZodIssueToFormErrorItem } from '@/utils/zod';
import { type FormError, isFormError } from './form';
import { getResourceFromSubject } from './getResourceFromSubject';

let intlInstance: IntlShape;

/**
 * Creates instance of intl to translate permission error notifications
 */
export function setRequestIntlInstance(intl: IntlShape) {
  intlInstance = intl;
}

/**
 * Parses the JSON returned by a network request
 */
function parseJSON<T>(response: Response): Promise<T> | null {
  if (response.status === 204 || response.status === 205) {
    return null;
  }
  return response.json().catch(() => null);
}

export class RequestError extends Error {
  constructor(
    public readonly response: Response,
    message?: string,
  ) {
    super(message || `HTTP ${response.status}`);
    this.name = 'RequestError';

    // https://github.com/Microsoft/TypeScript-wiki/blob/main/Breaking-Changes.md#extending-built-ins-like-error-array-and-map-may-no-longer-work
    Object.setPrototypeOf(this, RequestError.prototype);
  }
}

export class ValidationFailedError extends RequestError {
  // NOTE: vite can minify class names to reduce bundle size so we need to store original name as string literal
  static displayName = 'ValidationFailedError';

  constructor(
    public readonly errors: ReadonlyArray<Readonly<FormError>>,
    response: Response,
    message?: string,
  ) {
    super(response, message);
    this.name = ValidationFailedError.displayName;
    Object.setPrototypeOf(this, ValidationFailedError.prototype);
  }

  toJSON() {
    return {
      name: this.name,
      message: this.message,
      errors: this.errors,
    };
  }
}

export class ConflictError extends RequestError {
  // NOTE: vite can minify class names to reduce bundle size so we need to store original name as string literal
  static displayName = 'ConflictError';

  constructor(
    public readonly code: string,
    public override readonly response: Response,
    message?: string,
  ) {
    super(response, message);
    this.name = ConflictError.displayName;
    Object.setPrototypeOf(this, ConflictError.prototype);
  }

  public static isConflictErrorPayload(payload: unknown): payload is ConflictError {
    return (
      typeof payload === 'object' &&
      payload !== null &&
      'code' in payload &&
      typeof (payload as { code: unknown }).code === 'string'
    );
  }
}

export class ImportValidationError<T> extends RequestError {
  constructor(
    public readonly result: ImportResult<T>,
    response: Response,
    message?: string,
  ) {
    super(response, message);
    this.name = 'ImportValidationError';
    Object.setPrototypeOf(this, ImportValidationError.prototype);
  }
}

export class PayloadTooLargeError extends RequestError {
  constructor(
    public override readonly response: Response,
    message?: string,
  ) {
    super(response, message);
    this.name = 'PayloadTooLargeError';
    Object.setPrototypeOf(this, PayloadTooLargeError.prototype);
  }
}

/**
 * Checks if a network request came back fine, and throws an error if not
 */
async function checkStatus(response: Response): Promise<Response> {
  if (response.status >= 200 && response.status < 300) {
    return response;
  }
  if (response.status === 401) {
    toast.error(intlInstance.formatMessage(TRANSLATED_ERRORS[ErrorType.Unauthorized]), {
      toastId: 'network-error-401',
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  } else if (response.status === 403) {
    let message = response.statusText;

    const [mediaType] = response.headers.get('Content-Type')?.split('; ') ?? [];
    if (mediaType === 'application/json') {
      const payload = await response.json<{ error?: { code: string; message: string } }>();
      if (isCaslForbiddenError(payload.error)) {
        const action = payload.error?.context?.action;
        const subject = payload.error?.context?.subject;
        const resource = getResourceFromSubject(subject);
        message = resource
          ? intlInstance.formatMessage(TRANSLATED_ERRORS[ErrorType.CaslForbiddenError], {
              action: intlInstance.formatMessage(RESOURCE_ACTIONS_TRANSLATED[action]),
              resource: intlInstance.formatMessage(RESOURCES_TRANSLATED[resource]).toLowerCase(),
            })
          : payload.error.message;
      } else if (payload.error && typeof payload.error.message === 'string') {
        message = payload.error.message;
      }
    }

    toast.error(message, { toastId: 'network-error-403', position: toast.POSITION.BOTTOM_RIGHT });
  } else if (response.status === 409) {
    const possibleConflictError: unknown = await response.clone().json();

    if (ConflictError.isConflictErrorPayload(possibleConflictError)) {
      throw new ConflictError(possibleConflictError.code, response, response.statusText);
    }
  } else if (response.status === 412) {
    const errors: unknown = await response.clone().json();

    if (Array.isArray(errors) && errors.every(isZodIssue))
      throw new ValidationFailedError(errors.map(mapZodIssueToFormErrorItem), response, response.statusText);

    // TODO: https://enmontech.atlassian.net/browse/ENMON-1666
    if (Array.isArray(errors) && errors.every(isFormError))
      throw new ValidationFailedError(errors, response, response.statusText);
  } else if (response.status === 413) {
    toast.error(intlInstance.formatMessage(TRANSLATED_ERRORS[ErrorType.TooManyDataPoints]), {
      toastId: 'network-error-413',
      position: toast.POSITION.BOTTOM_RIGHT,
    });
    throw new PayloadTooLargeError(response, response.statusText);
  } else {
    toast.error(intlInstance.formatMessage(TRANSLATED_ERRORS[ErrorType.PreconditionFailed]), {
      toastId: 'network-error-other',
      position: toast.POSITION.BOTTOM_RIGHT,
    });
  }
  throw new RequestError(response, response.statusText);
}

export interface RequestOptions extends RequestInit {
  query?: ConstructorParameters<typeof URLSearchParams>[0];
  // NOTE: this property should be typed as explicit undefined, not just optional
  signal: Exclude<RequestInit['signal'], null> | undefined;
}

export default function request<T>(
  path: string | URL,
  options: RequestOptions = { signal: undefined },
  parseResponse: (response: Response) => Promise<T> | null = parseJSON,
): Promise<T | null> {
  const extendedOptions: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    ...options,
  };

  const url = new URL(path, config.apiBaseUrl === '/' ? document.location.origin : config.apiBaseUrl);

  if (options.query !== undefined && Object.keys(options.query).length !== 0) {
    url.search = stringify(options.query, { arrayFormat: 'indices' });
  }

  return fetch(url.href, extendedOptions).then(checkStatus).then<T | null>(parseResponse);
}

export function uploadFile<T>(
  path: string | URL,
  body: RequestInit['body'],
  options: Pick<RequestOptions, 'signal' | 'query'> & { method?: 'POST' | 'PUT' | 'PATCH' } = { signal: undefined },
): Promise<T | null> {
  const extendedOptions: RequestInit = {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
    },
    method: options?.method ?? 'POST',
    body,
  };

  const url = new URL(path, config.apiBaseUrl === '/' ? document.location.origin : config.apiBaseUrl);

  if (options.query !== undefined && Object.keys(options.query).length !== 0) {
    url.search = new URLSearchParams(options.query).toString();
  }

  return fetch(url.href, extendedOptions).then(checkStatus).then<T | null>(parseJSON);
}
